/**
 * Consent-gated Google Analytics 4 (gtag.js) loader.
 *
 * GA4 is treated as an analytics vendor and is therefore gated behind the same
 * consent rules as Better Stack and PostHog: the `gtag.js` script is only ever
 * requested once the user has granted the `analytics` consent bucket AND Global
 * Privacy Control is not signalled. We never inject the raw, unconditional
 * Google snippet into the document `<head>` - that would fire a pageview and set
 * the `_ga` identifier before consent, which the PureBox consent contract
 * forbids (see lib/consent.ts).
 *
 * Defense in depth: the shared gtag bootstrap sets Consent Mode v2 defaults to
 * `denied`, then GA4 flips only `analytics_storage` to `granted` on load.
 *
 * The measurement ID is a public, non-secret value (it ships in the client
 * bundle), supplied via `NEXT_PUBLIC_GA_MEASUREMENT_ID`. When unset or a
 * placeholder, the loader is a clean no-op so local dev and previews stay quiet.
 */

import { resolveDirectoryUtmFromRef } from './attribution';
import { detectGpc, hasConsentFor } from './consent';
import { ensureGtagBootstrap } from './gtag-core';
import { Logger } from './logger';

const PLACEHOLDER_ID_PATTERNS = [
  /^g-x+$/i,
  /^your-(ga-|google-)?measurement-id$/i,
];

const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;

/**
 * Internal app query parameters that must be stripped from the `page_location`
 * sent to GA4 so GA4 does not treat internal UI state (like `?source=pricing`)
 * as a campaign source override (`sessionSource = pricing`, `medium = (not set)`).
 * Also normalizes known directory `?ref=` parameters (e.g. `?ref=producthunt`,
 * `?ref=futuretools.io`) into `utm_source=<dir>&utm_medium=directory` so GA4
 * attributes them to directory referrals rather than burying them in `Direct`.
 */
const INTERNAL_QUERY_PARAMS_TO_STRIP_FROM_GA4 = ['source', 'from'] as const;

export const sanitizeGaPageLocation = (rawHref: string): string => {
  try {
    const parsed = new URL(rawHref);
    for (const key of INTERNAL_QUERY_PARAMS_TO_STRIP_FROM_GA4) {
      parsed.searchParams.delete(key);
    }
    const refParam = parsed.searchParams.get('ref');
    const directoryUtm = resolveDirectoryUtmFromRef(refParam);
    if (directoryUtm) {
      if (!parsed.searchParams.has('utm_source')) {
        parsed.searchParams.set('utm_source', directoryUtm.utmSource);
      }
      if (!parsed.searchParams.has('utm_medium')) {
        parsed.searchParams.set('utm_medium', directoryUtm.utmMedium);
      }
    }
    return parsed.toString();
  } catch {
    return typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : rawHref;
  }
};

let loaded = false;

const trimString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

export const resolveMeasurementId = (): string | undefined => {
  const trimmed = trimString(process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID);
  if (!trimmed) {
    return undefined;
  }
  if (PLACEHOLDER_ID_PATTERNS.some((pattern) => pattern.test(trimmed))) {
    return undefined;
  }
  if (!MEASUREMENT_ID_PATTERN.test(trimmed)) {
    return undefined;
  }
  return trimmed;
};

export const googleAnalyticsAllowed = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return hasConsentFor('analytics') && !detectGpc();
};

export const loadGoogleAnalytics = (): void => {
  if (typeof window === 'undefined' || loaded) {
    return;
  }
  if (!googleAnalyticsAllowed()) {
    return;
  }
  const measurementId = resolveMeasurementId();
  if (!measurementId) {
    return;
  }

  try {
    ensureGtagBootstrap(measurementId);
    if (!window.gtag) {
      return;
    }

    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
    });
    const currentHref = window.location.href;
    const sanitizedLocation = sanitizeGaPageLocation(currentHref);
    if (sanitizedLocation !== currentHref) {
      window.gtag('config', measurementId, {
        page_location: sanitizedLocation,
      });
    } else {
      window.gtag('config', measurementId);
    }
    loaded = true;
  } catch (error) {
    Logger.warn('Google Analytics failed to load', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

export const trackGoogleAnalyticsPageView = (url: string, title?: string): void => {
  if (typeof window === 'undefined' || !loaded || !window.gtag) {
    return;
  }
  if (!googleAnalyticsAllowed()) {
    return;
  }
  const measurementId = resolveMeasurementId();
  if (!measurementId) {
    return;
  }

  try {
    const pageLocation = sanitizeGaPageLocation(window.location.href);
    const pageTitle = title || (typeof document !== 'undefined' ? document.title : '');
    window.gtag('event', 'page_view', {
      page_path: url,
      page_location: pageLocation,
      page_title: pageTitle,
      send_to: measurementId,
    });
  } catch (error) {
    Logger.warn('Google Analytics failed to track page view', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

export const __resetGoogleAnalyticsForTests = (): void => {
  loaded = false;
};
